import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Compass, ChevronUp, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { FeedSlide } from "@/components/feed/FeedSlide";

// "Para ti" oculto até à Fase 3 (personalização); o backend continua a aceitar tab=for_you.
const TABS = [["recent", "feed_recent"], ["communities", "feed_communities"]];
const VALID = new Set(TABS.map(([id]) => id));
const keyOf = (i) => `${i.type}:${i.id}`;

function useViewportHeight(ref) {
  const [h, setH] = useState(0);
  useLayoutEffect(() => {
    const apply = () => {
      const el = ref.current; if (!el) return;
      const a = document.activeElement;
      if (a && /^(INPUT|TEXTAREA)$/.test(a.tagName)) return; // teclado aberto (iOS): não re-dimensionar o feed
      const nav = document.querySelector('[data-testid="bottom-nav"]');
      const navH = nav && nav.dataset.hidden !== "true" ? nav.offsetHeight : 0;
      setH(Math.max(240, Math.round((window.visualViewport?.height || window.innerHeight) - el.getBoundingClientRect().top - navH)));
    };
    apply();
    const onBlur = () => setTimeout(() => { window.scrollTo(0, 0); apply(); }, 150); // iOS desloca a página para mostrar o input; repõe o topo ao fechar o teclado
    window.addEventListener("resize", apply); window.visualViewport?.addEventListener("resize", apply); document.addEventListener("focusout", onBlur);
    const t = setTimeout(apply, 300);
    return () => { window.removeEventListener("resize", apply); window.visualViewport?.removeEventListener("resize", apply); document.removeEventListener("focusout", onBlur); clearTimeout(t); };
  }, [ref]);
  return h;
}

export default function Feed() {
  const { user } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState(() => { const s = localStorage.getItem("feed_tab"); return VALID.has(s) ? s : "recent"; });
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [active, setActive] = useState(null);
  const [muted, setMuted] = useState(() => localStorage.getItem("feed_muted") !== "0");
  const wrap = useRef(null);
  const scroller = useRef(null);
  const height = useViewportHeight(wrap);
  const slideEls = useRef(new Map());
  const obs = useRef(null);

  useEffect(() => {
    const on = (e) => setItems((prev) => prev.filter((i) => (i.author?.id || i.author_id) !== e.detail?.userId));
    window.addEventListener("auk:user-blocked", on);
    return () => window.removeEventListener("auk:user-blocked", on);
  }, []);

  const load = useCallback(async (cur, reset = false) => {
    setBusy(true);
    try {
      const { data } = await api.get("/feed", { params: { tab, cursor: cur || undefined } });
      const cids = data.items.filter((i) => i.type === "community").map((i) => i.id);
      const pids = data.items.filter((i) => i.type === "profile").map((i) => i.id);
      const [mineC, mineP] = await Promise.all([
        cids.length ? api.get("/community-reactions/mine", { params: { post_ids: cids.join(",") } }).then((r) => r.data || {}).catch(() => ({})) : {},
        pids.length ? api.get("/profile-reactions/mine", { params: { post_ids: pids.join(",") } }).then((r) => r.data || {}).catch(() => ({})) : {},
      ]);
      const fresh = data.items.map((i) => ({ ...i, mine: (i.type === "community" ? mineC : mineP)[i.id] ?? null }));
      setItems((prev) => {
        const base = reset ? [] : prev;
        const seen = new Set(base.map(keyOf));
        return [...base, ...fresh.filter((i) => !seen.has(keyOf(i)))];
      });
      setCursor(data.next_cursor); setDone(!data.next_cursor);
    } catch { setDone(true); }
    finally { setBusy(false); }
  }, [tab]);

  useEffect(() => { localStorage.setItem("feed_tab", tab); setItems([]); setCursor(null); setDone(false); setActive(null); scroller.current?.scrollTo({ top: 0 }); load(null, true); }, [tab, load]);

  // Publicação ativa = a que ocupa ≥ 60% do ecrã; só essa reproduz vídeo
  useEffect(() => {
    const root = scroller.current;
    if (!root || typeof IntersectionObserver === "undefined") { setActive(items[0] ? keyOf(items[0]) : null); return; }
    obs.current?.disconnect();
    obs.current = new IntersectionObserver((es) => { es.forEach((e) => { if (e.isIntersecting && e.intersectionRatio >= 0.6) setActive(e.target.dataset.key); }); }, { root, threshold: [0.6] });
    slideEls.current.forEach((el) => obs.current.observe(el));
    return () => obs.current?.disconnect();
  }, [items]);

  // Pré-carrega mais quando faltam ≤ 3 publicações
  useEffect(() => {
    const idx = items.findIndex((i) => keyOf(i) === active);
    if (idx >= 0 && idx >= items.length - 3 && cursor && !busy) load(cursor);
  }, [active, items, cursor, busy, load]);

  const step = useCallback((dir) => { const el = scroller.current; if (el) el.scrollBy({ top: dir * el.clientHeight, behavior: "smooth" }); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.target.closest("input,textarea,[contenteditable]")) return; if (e.key === "ArrowDown" || e.key === "PageDown") { e.preventDefault(); step(1); } if (e.key === "ArrowUp" || e.key === "PageUp") { e.preventDefault(); step(-1); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  const toggleMute = () => setMuted((m) => { localStorage.setItem("feed_muted", m ? "0" : "1"); return !m; });
  const setSlideRef = (key) => (el) => { if (el) slideEls.current.set(key, el); else slideEls.current.delete(key); };
  const activeIdx = items.findIndex((i) => keyOf(i) === active);

  return (
    <div ref={wrap} data-testid="feed-page" data-active-index={activeIdx} style={{ height: height || undefined }} className="relative bg-[#0d0d0d] select-none">
      <div className="relative mx-auto h-full w-full md:max-w-[min(100%,calc(var(--feed-h)*0.5625))]" style={{ "--feed-h": `${height}px` }}>
      {/* Separadores sempre sobre o conteúdo (fora do scroller, dentro da coluna) */}
      <div className="absolute inset-x-0 top-0 z-20 h-14 pointer-events-none" data-testid="feed-topbar">
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/75 via-black/35 to-transparent" aria-hidden="true" />
        <div className="absolute inset-x-0 top-2 flex items-center justify-center gap-8 pointer-events-auto" data-testid="feed-tabs">
          {TABS.map(([id, key]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`feed-tab-${id}`} data-active={tab === id}
                    className={`relative pb-1.5 text-[17px] font-semibold drop-shadow-md transition-colors ${tab === id ? "text-white" : "text-white/60"}`}>
              {t(key)}
              {tab === id && <span className="absolute left-1/2 -translate-x-1/2 bottom-0 h-[3px] w-10 rounded-full bg-[#c8102e]" aria-hidden="true" />}
            </button>
          ))}
        </div>
        <Link to="/explorar" data-testid="feed-explore-link" aria-label={t("explore")} className="absolute right-3 top-2 h-10 w-10 inline-flex items-center justify-center rounded-full text-white/90 drop-shadow-md pointer-events-auto"><Compass size={24} /></Link>
      </div>
      <div ref={scroller} data-testid="feed-scroller" className="absolute inset-0 overflow-y-auto snap-y snap-mandatory overscroll-contain scrollbar-none">
        {items.map((it) => (
          <FeedSlide key={keyOf(it)} item={it} user={user} active={active === keyOf(it)} muted={muted} onToggleMute={toggleMute} slideRef={setSlideRef(keyOf(it))}
                     onRemoved={(x) => setItems((prev) => prev.filter((i) => keyOf(i) !== keyOf(x)))} />
        ))}
        {!busy && items.length === 0 && done && (
          <div className="h-full snap-start flex items-center justify-center text-center text-sm text-white/70 px-8" data-testid="feed-empty">{t("feed_empty")}</div>
        )}
        {busy && items.length === 0 && <div className="h-full snap-start flex items-center justify-center" data-testid="feed-loading"><Loader2 size={24} className="animate-spin text-white/60" /></div>}
        {done && items.length > 0 && <div className="h-16 snap-start flex items-center justify-center text-xs text-white/40" data-testid="feed-end">{t("feed_end")}</div>}
      </div>
      </div>

      <div className="hidden md:flex fixed right-6 bottom-24 flex-col gap-2 z-30" data-testid="feed-arrows">
        <button type="button" onClick={() => step(-1)} aria-label="Anterior" data-testid="feed-prev" className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/30 text-white inline-flex items-center justify-center"><ChevronUp /></button>
        <button type="button" onClick={() => step(1)} aria-label="Seguinte" data-testid="feed-next" className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/30 text-white inline-flex items-center justify-center"><ChevronDown /></button>
      </div>
    </div>
  );
}
